"use client";

import type { ReactNode } from "react";
import PageContent from "@/components/page-content";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";

type Faq = {
  question: string;
  answer: ReactNode;
};

const faqs: Faq[] = [
  {
    question: "How is billing calculated on the Standard Plan?",
    answer: (
      <>
        You pre-purchase tokens (credits). As you make requests, we deduct the
        exact number of input and output tokens used. When your balance runs low,
        you can top up instantly. If you prefer, you can also use our &ldquo;auto-top-up&rdquo;
        feature to automatically top-up your account when it falls below a specialized
        amount.
      </>
    ),
  },
  {
    question: "Do tokens/credits expire?",
    answer: (
      <>
        Yes. Prepaid tokens are valid for <strong>12 months</strong> from the
        purchase date. Unused tokens <strong>expire</strong> at the end of that
        period and are <strong>non-refundable</strong> (except where required by
        law).
      </>
    ),
  },
  {
    question: "Do you train on my data?",
    answer: <>No. Your request data is <strong>never</strong> used to train our models.</>,
  },
  {
    question: "How long do you keep my data?",
    answer: (
      <>
        Operational logs are retained <strong>only as needed</strong> to run and
        secure the service. Enterprise customers can request stricter retention
        or zero-retention options, and on-prem/VPC deployments keep data within
        your environment.
      </>
    ),
  },
  {
    question: 'What are "dense tokens" and why do they matter?',
    answer: (
      <>
        Moondream uses a denser tokenization scheme (superBPE) plus dedicated grounding tokens that represent x,y coordinates and
        bounding boxes. This means we generate answers using fewer tokens, making Moondream both faster and cheaper to run. You get
        quicker responses and lower costs per task compared to standard vision models.
      </>
    ),
  },
  {
    question: "Can I deploy on-prem or in my own VPC?",
    answer: (
      <>
        Yes! You can download and run Moondream locally (see our <a className="underline" href="https://docs.moondream.ai/running-locally">running locally guide)</a>.
        For even better performance, Moondream Cloud uses a custom inference engine optimized specifically for Moondream, delivering
        <em>4-5x faster inference</em>. If you want to deploy this optimized engine on-prem or in your own VPC,&nbsp;
        <a className="underline" href="mailto:contact@moondream.ai">contact us</a> about an <span className="bold">Enterprise Plan</span>.
      </>
    ),
  },
  {
    question: "What happens if I hit throughput limits?",
    answer: (
      <>
        You may see rate-limit errors on the Standard Plan. Reach out and we&apos;ll
        right-size your limits; the Enterprise Plan provides higher or dedicated
        capacity aligned to your needs.
      </>
    ),
  },
  {
    question: "Are you cheaper than other token-based services?",
    answer: (
      <>
        Often, yes for grounding-heavy workloads&mdash;because dense tokens typically
        mean <strong>fewer tokens per request</strong>. Your actual cost depends
        on usage patterns and response sizes.
      </>
    ),
  },
  {
    question: "Can I switch to Enterprise later?",
    answer: (
      <>Absolutely. Start on Standard and talk to Sales any time to migrate to an Enterprise agreement.</>
    ),
  },
];

export function PricingFaq() {
  return (
    <PageContent className="pb-16">
      <div className="flex flex-col items-center gap-6 text-center">
        <Badge className="text-xs font-semibold uppercase tracking-wide">
          FAQ
        </Badge>
        <h2 className="text-3xl font-semibold tracking-tight md:text-4xl">
          Answers for the most common questions
        </h2>
      </div>

      <Accordion type="single" collapsible className="mt-10 space-y-4">
        {faqs.map((faq, index) => (
          <AccordionItem key={faq.question} value={`item-${index}`} className="rounded-xl border px-4">
            <AccordionTrigger className="text-left text-base font-medium md:text-lg">
              {faq.question}
            </AccordionTrigger>
            <AccordionContent forceMount className="text-left text-sm text-muted-foreground md:text-base">
              <div className="space-y-3">{faq.answer}</div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </PageContent>
  );
}
